import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ErpTemplate from '../../components/ErpTemplate';
import { FinCheck, FinField, FinInput } from '../../components/FinField';
import ReactSelect from '../../components/ReactSelect';
import api from '../../services/api';
import notify from '../../services/notify';
import '../../css/financeiro-parcela-pagar.css';

const TIPOS = [
    { value: 'disparo', label: 'Disparo — a data que ancora o alerta' },
    { value: 'pendencia', label: 'Pendência — o que dispensa o alerta' },
];

const SEM_CAMPO = '__exists__';

export default function CadastroSlaCriterio({ match }) {
    const id = match.params.id;
    const token = localStorage.getItem('token');
    const history = useHistory();

    const [dados, setDados] = useState({ tipo: 'pendencia', ativo: true });
    const [origens, setOrigens] = useState([]);
    // Cadastro novo não tem buscar() para segurar o processando geral, e o select de Origem
    // fica vazio até este fetch voltar — sem um estado próprio ele pisca "Sem opções" antes de
    // popular, que lê como bug em vez de carregamento.
    const [carregandoOrigens, setCarregandoOrigens] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!id);

    const origemAtual = useMemo(
        () => origens.find((item) => item.origem === dados.origem) ?? null,
        [origens, dados.origem]
    );

    /**
     * A lista de campos vem do backend já separada por papel: disparo só recebe coluna de data,
     * porque é ela que serve de âncora. Quem aplica essa regra é a API — repeti-la aqui criaria
     * duas verdades sobre a mesma coisa.
     */
    const camposDisponiveis = useMemo(() => {
        if (!origemAtual) return [];
        const lista = dados.tipo === 'disparo' ? origemAtual.disparo : origemAtual.pendencia;
        const opcoes = (lista ?? []).map((item) => ({
            value: item.campo,
            label: `${item.campo} (${item.tipo})`
        }));

        // Pendência sem campo = "o registro existe?". Só faz sentido quando a origem tem relação
        // própria: o próprio evento existe sempre.
        if (dados.tipo === 'pendencia' && origemAtual.aceita_exists) {
            return [{ value: SEM_CAMPO, label: 'Apenas verificar se o registro existe' }, ...opcoes];
        }

        return opcoes;
    }, [origemAtual, dados.tipo]);

    const buscar = () => {
        if (!id) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        api({ method: 'get', action: `/slaCriterio/${id}`, token })
            .then((response) => {
                const recebido = response.data.data ?? {};
                setDados({ ...recebido, campo: recebido.campo || SEM_CAMPO });
            })
            .catch((error) => {
                // Sem isto a tela abre em branco como se fosse um cadastro novo, e o operador
                // cadastra duplicado achando que o registro sumiu.
                toast.error('Erro ao carregar o critério: ' + (error.response?.data?.error ?? error.message ?? ''));
            })
            .finally(() => setIsLoading(false));
    };

    const salvar = () => {
        if (isSubmitting) return;

        if (!dados.nome || !dados.origem || !dados.tipo) {
            toast.error('Preencha nome, tipo e origem.');
            return;
        }
        if (dados.tipo === 'disparo' && (!dados.campo || dados.campo === SEM_CAMPO)) {
            toast.error('Critério de disparo exige um campo de data.');
            return;
        }

        setIsSubmitting(true);

        const payload = {
            nome: dados.nome,
            tipo: dados.tipo,
            origem: dados.origem,
            campo: dados.campo === SEM_CAMPO ? null : (dados.campo || null),
            ativo: !!dados.ativo,
        };

        const requisicao = id
            ? { method: 'put', action: `/slaCriterio/${id}`, data: payload }
            : { method: 'post', action: '/slaCriterio', data: payload };

        api({ ...requisicao, token })
            .then(() => {
                toast.success('Dados salvos com sucesso!');
                history.push('/slaCriterio');
            })
            .catch((error) => {
                notify.apiError(error, 'Erro ao salvar.');
            })
            .finally(() => setIsSubmitting(false));
    };

    useEffect(() => {
        api({ method: 'get', action: '/slaCriterio/origens', token })
            .then((response) => setOrigens(response.data.data ?? []))
            .catch((error) => {
                setOrigens([]);
                // Sem isto o 403 vira um select vazio, e o sintoma ("Sem opções") não tem nada a
                // ver com a causa (perfil sem acesso ao catálogo).
                toast.error(error.response?.status === 403
                    ? 'Sem permissão para listar as origens — o catálogo de critérios é restrito ao perfil admin.'
                    : 'Não foi possível carregar as origens: ' + (error.response?.data?.msg ?? error.message ?? ''));
            })
            .finally(() => setCarregandoOrigens(false));
        buscar();
        // eslint-disable-next-line
    }, []);

    const opcoesOrigem = origens.map((item) => ({ value: item.origem, label: item.nome }));

    return (
        <ErpTemplate processando={isLoading}>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            Critério de SLA
                            <span className="fin-pp__pill is-warn" title="Esta tela lista colunas do banco e é restrita ao perfil admin">
                                Somente admin
                            </span>
                        </h1>
                        <p className="fin-pp__subtitle">{dados.nome || 'Novo critério'}</p>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm" onClick={() => history.push('/slaCriterio')}>
                            Voltar
                        </button>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill" onClick={salvar} disabled={isSubmitting}>
                            {isSubmitting ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>

                <form className="form" onSubmit={(e) => { salvar(); e.preventDefault(); }}>
                    <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-title">Definição</span>
                            <span className="fin-pp__cphead-sub">A origem é uma relação do evento; o campo é lido do banco.</span>
                        </div>
                        <div className="fin-pp__filters">
                            <FinField label="Nome" required full>
                                <FinInput
                                    id="nome"
                                    value={dados.nome ?? ''}
                                    placeholder="Data de fim do evento"
                                    onChange={(e) => setDados({ ...dados, nome: e.target.value })}
                                    style={{ paddingLeft: 14 }}
                                />
                            </FinField>

                            <FinField label="Tipo" required>
                                <ReactSelect
                                    value={TIPOS.find((item) => item.value === dados.tipo) ?? null}
                                    // Trocar o tipo muda a lista de campos válidos, então o campo é zerado
                                    // junto — senão ficaria um campo de texto selecionado num disparo.
                                    onChange={(e) => setDados({ ...dados, tipo: e?.value ?? '', campo: '' })}
                                    options={TIPOS}
                                />
                            </FinField>

                            <FinField label="Origem" required>
                                {/* key força remontar quando a lista chega da API: sem ela o
                                    react-select fica com o snapshot vazio do primeiro render
                                    (o ReactSelect entra no ramo de options fixas porque [] é
                                    truthy) e mostra "Sem opções" para sempre. */}
                                <ReactSelect
                                    key={`origem-${opcoesOrigem.length}`}
                                    value={opcoesOrigem.find((item) => item.value === dados.origem) ?? null}
                                    onChange={(e) => setDados({ ...dados, origem: e?.value ?? '', campo: '' })}
                                    options={opcoesOrigem}
                                    isDisabled={carregandoOrigens}
                                    placeholder={carregandoOrigens ? 'Carregando origens...' : 'Selecione...'}
                                />
                            </FinField>

                            <FinField label="Campo" required={dados.tipo === 'disparo'} grow>
                                <ReactSelect
                                    key={`campo-${dados.origem ?? ''}-${dados.tipo ?? ''}-${camposDisponiveis.length}`}
                                    value={camposDisponiveis.find((item) => item.value === dados.campo) ?? null}
                                    onChange={(e) => setDados({ ...dados, campo: e?.value ?? '' })}
                                    options={camposDisponiveis}
                                    isDisabled={!dados.origem}
                                />
                            </FinField>

                            <FinField label="Situação">
                                <FinCheck
                                    label="Ativo"
                                    checked={!!dados.ativo}
                                    onChange={(e) => setDados({ ...dados, ativo: e.target.checked })}
                                />
                            </FinField>
                        </div>

                        <p className="fin-pp__cphead-sub" style={{ marginTop: 4 }}>
                            {dados.tipo === 'disparo'
                                ? 'Disparo lista apenas colunas de data — é a partir dela que o prazo do alerta é contado.'
                                : 'Pendência aceita qualquer campo: preenchido significa cumprido, e o alerta não sai.'}
                        </p>
                    </div>
                </form>
            </div>
        </ErpTemplate>
    );
}
